import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin from '@draft-js-plugins/mention';
import { EditorState, Modifier } from 'draft-js';
import { DecoratorComponent, DropdownHeader, InfoIcon, ResetBtn, SuggestionEntryComponent } from './comps';
import {
  createEditorStateFromExpression,
  customSuggestionsFilter,
  DEFAULT_FORMULA_ERROR_MESSAGE,
  evaluate,
  expressionToFormula,
  handleKeyCommand,
  INVALID_SYNTAX_FORMULA_ERROR_MESSAGE,
  isWorkspaceOrGlobalVariable,
  serializeEditorStateToExpression,
  validateFormulaSyntax,
} from './util';
import { Poppins, Spacer, Tooltip } from 'src/common';
import { Variable } from 'src/api/types';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Div } from './styled';
import { SubMentionComponentProps } from '@draft-js-plugins/mention/lib/Mention';
import { useStateSelector } from 'src/redux';
import { formatCurrency } from 'src/utils/misc';

export const EditorStateContext = React.createContext<{
  editorState: EditorState;
}>({ editorState: EditorState.createEmpty() });

export const SuggestionEditingContext = React.createContext<{
  isEditingRef: React.MutableRefObject<boolean>;
  editingVariableId: number | null;
  setEditingVariableId: (id: number | null) => void;
  cancelEditing: () => void;
  closeDropdown: () => void;
  onSaveVariableValue?: (config: { variableId: number; value: number; currency: boolean }) => void | Promise<void>;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  editingInputValue: number | undefined;
  setEditingInputValue: (value: number | undefined) => void;
  editingCurrency: boolean;
  setEditingCurrency: (currency: boolean) => void;
  insertVariableToFormula: (variable: Variable) => void;
  workspaceVariableNames: Set<string>;
}>({
  isEditingRef: { current: false },
  editingVariableId: null,
  setEditingVariableId: () => {},
  cancelEditing: () => {},
  closeDropdown: () => {},
  onSaveVariableValue: undefined,
  isSaving: false,
  setIsSaving: () => {},
  editingInputValue: undefined,
  setEditingInputValue: () => {},
  editingCurrency: false,
  setEditingCurrency: () => {},
  insertVariableToFormula: () => {},
  workspaceVariableNames: new Set(),
});

export const DecoratorWithContext: React.FC<SubMentionComponentProps> = (props) => {
  const { editorState } = useContext(EditorStateContext);
  const { workspaceVariableNames } = useContext(SuggestionEditingContext);
  const contentState = editorState.getCurrentContent();

  return <DecoratorComponent {...props} contentState={contentState} workspaceVariableNames={workspaceVariableNames} />;
};

interface FormulaInputProps {
  variables: Variable[];
  initaialExpression?: string | null;
  onChange: (expression: string) => void;
  onEval: (value: number) => void;
  error?: boolean;
  onErr?: (err: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  helperText?: string | false;
  InputAdornmentStart?: React.ReactNode;
  dataCy?: string;
  onReset?: () => void;
  customResultComponent?: JSX.Element | false;
  hideResult?: boolean;
  onSaveVariableValue?: (config: { variableId: number; value: number; currency: boolean }) => void | Promise<void>;
  onAddWorkspaceVariable?: () => void;
  onAddAssessmentVariable?: () => void;
}

const FormulaInput: React.FC<FormulaInputProps> = ({
  initaialExpression = '',
  onChange,
  onEval,
  error,
  onErr,
  onFocus,
  onBlur,
  helperText,
  InputAdornmentStart,
  dataCy,
  variables,
  onReset,
  customResultComponent,
  hideResult,
  onSaveVariableValue,
  onAddWorkspaceVariable,
  onAddAssessmentVariable,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [suggestions, setSuggestions] = useState<Variable[]>([]);
  const [editorState, setEditorState] = useState<EditorState>(() =>
    createEditorStateFromExpression({
      expression: initaialExpression || '',
      data: variables,
    }),
  );
  const [open, setOpen] = useState(false);
  const ref = useRef<Editor>(null);
  const evalValue = useRef(0);
  const mexpErr = useRef<string | null>(null);
  const isEditingRef = useRef(false);
  const [editingVariableId, setEditingVariableId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingInputValue, setEditingInputValue] = useState<number | undefined>(undefined);
  const [editingCurrency, setEditingCurrency] = useState(false);

  const workspaceVariableNames = useMemo(() => {
    return new Set(variables.filter((v) => isWorkspaceOrGlobalVariable(v)).map((v) => v.name));
  }, [variables]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const dropdownEl = document.querySelector('.mnw6qvm');
      const editorEl = document.querySelector('.fi-root');
      const target = e.target as Node;

      const isOutsideDropdown = !dropdownEl || !dropdownEl.contains(target);
      const isOutsideEditor = !editorEl || !editorEl.contains(target);

      if (isOutsideDropdown && isOutsideEditor) {
        isEditingRef.current = false;
        setEditingVariableId(null);
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const setEditingVariableIdWithRef = useCallback((id: number | null) => {
    setEditingVariableId(id);
    isEditingRef.current = id !== null;
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingVariableId(null);
  }, []);

  const closeDropdown = useCallback(() => {
    isEditingRef.current = false;
    setEditingVariableId(null);
    setOpen(false);
  }, []);

  const insertVariableToFormula = useCallback(
    (variable: Variable) => {
      const contentState = editorState.getCurrentContent();
      let selection = editorState.getSelection();

      const blockKey = selection.getStartKey();
      const block = contentState.getBlockForKey(blockKey);
      const text = block.getText();
      const cursorOffset = selection.getStartOffset();

      let triggerStart = cursorOffset;
      for (let i = cursorOffset - 1; i >= 0; i--) {
        if (text[i] === '#') {
          triggerStart = i;
          break;
        }
        if (text[i] === ' ' && i < cursorOffset - 1) {
          break;
        }
      }

      if (triggerStart < cursorOffset && text[triggerStart] === '#') {
        selection = selection.merge({
          anchorOffset: triggerStart,
          focusOffset: cursorOffset,
        }) as typeof selection;
      }

      const contentStateWithEntity = contentState.createEntity('#mention', 'SEGMENTED', {
        mention: { ...variable },
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const mentionText = variable.alias || variable.name;
      const newContentState = Modifier.replaceText(
        contentStateWithEntity,
        selection,
        mentionText,
        undefined,
        entityKey,
      );

      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter()));

      setOpen(false);
      isEditingRef.current = false;
      setEditingVariableId(null);

      mpEvent(MPEvents.VariableAddedToFormula, {
        location: 'Modal:Projected risk:Scenario',
        variableType: isWorkspaceOrGlobalVariable(variable) ? 'Workspace Variable' : 'Assessment Variable',
      });
    },
    [editorState],
  );

  const { MentionSuggestions, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      mentionTrigger: '#',
      mentionPrefix: '#',
      entityMutability: 'SEGMENTED',
      mentionComponent: DecoratorWithContext,
    });
    const { MentionSuggestions } = mentionPlugin;
    const plugins = [mentionPlugin];
    return { plugins, MentionSuggestions };
  }, []);

  useEffect(() => {
    const expression = serializeEditorStateToExpression(editorState);
    onChange(expression);
    const formula = expressionToFormula(expression, variables);
    if (formula) {
      const isSyntaxValid = validateFormulaSyntax(formula);
      if (!isSyntaxValid) {
        const message = INVALID_SYNTAX_FORMULA_ERROR_MESSAGE;
        mexpErr.current = message;
        onErr && onErr(message);
      } else {
        evaluate(formula)
          .then((val) => {
            onEval(val);
            onErr && onErr('');
            evalValue.current = val;
            mexpErr.current = null;
          })
          .catch(() => {
            const message = DEFAULT_FORMULA_ERROR_MESSAGE;
            mexpErr.current = message;
            onErr && onErr(message);
          });
      }
    } else {
      onEval(0);
      onErr && onErr('');
      mexpErr.current = '';
      evalValue.current = 0;
    }
  }, [variables, editorState]);

  const handleBeforeInput = useCallback((chars: string, editorState: EditorState) => {
    const selection = editorState.getSelection();
    const text = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getText();
    const position = selection.getStartOffset();

    if (chars === '#' && text[position - 1] && text[position - 1] !== ' ') {
      const newState = Modifier.insertText(
        editorState.getCurrentContent(),
        selection,
        ' #',
        editorState.getCurrentInlineStyle(),
        undefined,
      );

      setEditorState(EditorState.push(editorState, newState, 'insert-characters'));

      return 'handled';
    }
    return 'not-handled';
  }, []);

  const handleReset = useCallback(() => {
    const initialEditorState = createEditorStateFromExpression({
      expression: initaialExpression || '',
      data: variables,
    });

    setEditorState(EditorState.createWithContent(initialEditorState.getCurrentContent()));
  }, [initaialExpression, variables]);

  const handleReinit = useCallback(() => {
    const initialEditorState = createEditorStateFromExpression({
      expression: serializeEditorStateToExpression(editorState),
      data: variables,
    });

    setEditorState(EditorState.createWithContent(initialEditorState.getCurrentContent()));
  }, [editorState, variables]);

  useEffect(() => {
    handleReinit();
  }, [variables]);

  return (
    <EditorStateContext.Provider value={{ editorState }}>
      <SuggestionEditingContext.Provider
        value={{
          isEditingRef,
          editingVariableId,
          setEditingVariableId: setEditingVariableIdWithRef,
          cancelEditing,
          closeDropdown,
          onSaveVariableValue,
          isSaving,
          setIsSaving,
          editingInputValue,
          setEditingInputValue,
          editingCurrency,
          setEditingCurrency,
          insertVariableToFormula,
          workspaceVariableNames,
        }}
      >
        <Div $error={error}>
          <div
            className="fi-root"
            onClick={() => {
              ref.current!.focus();
              onFocus && onFocus();
            }}
            onBlur={() => {
              onBlur && onBlur();
            }}
          >
            {InputAdornmentStart && <div className="fi-root__adorment-start">{InputAdornmentStart}</div>}
            <div className="fi-root__editor" data-cy={dataCy}>
              <Editor
                editorKey={'editor'}
                editorState={editorState}
                onChange={setEditorState}
                plugins={plugins}
                ref={ref}
                handleBeforeInput={handleBeforeInput}
                handleKeyCommand={(key, state) => handleKeyCommand(key, state, setEditorState)}
              />
            </div>
            <MentionSuggestions
              open={open}
              onOpenChange={(newOpen) => {
                if (!newOpen && isEditingRef.current) {
                  return;
                }
                setOpen(newOpen);
                if (newOpen) {
                  mpEvent(MPEvents.FormulaVariablesOpen, {
                    location: 'Modal:Projected risk:Scenario',
                  });
                }
              }}
              suggestions={suggestions}
              onSearchChange={({ value }) => setSuggestions(customSuggestionsFilter(value, variables) as Variable[])}
              entryComponent={({ mention, ...props }) => (
                <SuggestionEntryComponent {...props} mention={mention as Variable} />
              )}
              onAddMention={(v: Variable) => {
                isEditingRef.current = false;
                setEditingVariableId(null);
                mpEvent(MPEvents.VariableAddedToFormula, {
                  location: 'Modal:Projected risk:Scenario',
                  variableType: isWorkspaceOrGlobalVariable(v) ? 'Workspace Variable' : 'Assessment Variable',
                });
              }}
            />
            <DropdownHeader
              open={open}
              onAddWorkspaceVariable={onAddWorkspaceVariable}
              onAddAssessmentVariable={onAddAssessmentVariable}
            />
          </div>
          {helperText && (
            <Poppins px={14} className="fi-helper-text">
              {helperText}
            </Poppins>
          )}

          {!hideResult && (
            <>
              <Spacer $px={14} />
              <div className="fi-result">
                <Tooltip
                  dataId="123"
                  place="bottom"
                  dataHtml={`
          Enter a numeric value or a formula.</br>
          E.g: 12500 - 5.5 * ( 20000 / 35 ) + 1000</br>
          Allowed characters are numbers and + - / * ( ) .</br>
          Press # to open variables.
          `}
                >
                  <InfoIcon />
                </Tooltip>
                {customResultComponent ? (
                  customResultComponent
                ) : (
                  <Poppins px={14}>
                    Result:{' '}
                    <span
                      css={`
                        color: ${mexpErr.current === DEFAULT_FORMULA_ERROR_MESSAGE ||
                        mexpErr.current === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE
                          ? colors.error
                          : colors.prussianBlue};
                      `}
                    >
                      {mexpErr.current ? mexpErr.current : formatCurrency(evalValue.current)}
                    </span>
                  </Poppins>
                )}
                {onReset && <ResetBtn onClick={handleReset} />}
              </div>
            </>
          )}
        </Div>
      </SuggestionEditingContext.Provider>
    </EditorStateContext.Provider>
  );
};

export default FormulaInput;
